/* eslint-disable react/prop-types */
/**
 * Collapsible map band on the deals list, between the filter row and the
 * grid: the tracts of the deals the grid is showing (DealMap in set mode).
 *
 * Collapsed by default, and the choice is remembered (utils/uiPrefs), like
 * the KPI strip and the filter row. The map is mounted only while the band is
 * open, so a user who keeps it folded pays for no Mapbox token or section
 * fetch. The deals, tracts, metadata and broker names come from the page
 * (`data`, passed straight to DealMap), so opening the band refetches none
 * of them.
 *
 * It sits inside the deals list's measured header block, so opening it
 * shortens the grid by itself (see tableScroll in pages/dealsList).
 */
import { useCallback, useState } from "react";
import { Button, Card, Flex, Typography } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";

import DealMap from "./dealMap/DealMap";
import { MAP_COLLAPSED, readFlag, writeFlag } from "../utils/uiPrefs";

// Room for the grid on a 768px laptop with filters and metrics open, and a
// whole county on a 1080p screen.
const BAND_MAP_HEIGHT = "clamp(240px, 32vh, 360px)";

const DealsMapBand = ({ visibleDealIds, atRowCap, rowCap, data }) => {
  // Read during useState init so the band renders folded instead of
  // flashing open and shutting.
  const [collapsed, setCollapsed] = useState(() => readFlag(MAP_COLLAPSED, true));

  const toggleCollapsed = useCallback(() => {
    setCollapsed((previous) => {
      writeFlag(MAP_COLLAPSED, !previous);
      return !previous;
    });
  }, []);

  return (
    <Card
      size="small"
      styles={{ body: { padding: "6px 10px" } }}
      style={{ marginBottom: 8, marginInline: 4, width: "99%" }}
      data-testid="deals-map-band"
    >
      {/* The header row stays put open or shut, so the chevron never moves. */}
      <Flex align="center" gap={4} wrap="wrap">
        <Button
          type="text"
          size="small"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Show map" : "Hide map"}
          icon={collapsed ? <RightOutlined /> : <DownOutlined />}
          onClick={toggleCollapsed}
        />
        <Typography.Text type="secondary">Map</Typography.Text>
        {!collapsed && atRowCap && visibleDealIds && (
          // Same cap as the grid and the metrics: a full page means the
          // search had more matches than it returned. Unfiltered, the map
          // is not capped (null = every deal the viewer can see).
          <Typography.Text type="warning" style={{ fontSize: 12, marginInlineStart: 8 }}>
            Shows the first {rowCap} matching deals — narrow the filter to see the rest
          </Typography.Text>
        )}
      </Flex>
      {!collapsed && (
        <div style={{ marginTop: 6 }}>
          <DealMap dealIds={visibleDealIds} height={BAND_MAP_HEIGHT} data={data} />
        </div>
      )}
    </Card>
  );
};

export default DealsMapBand;
